import { useState } from 'react';
import { Modal, Form, Input, message, Alert, Button, Popconfirm } from 'antd';
import { useTranslate } from '@tolgee/react';
import { tradeOfferV1, ITradeOfferV1Req } from 'services/api/return';
import { formatNumber } from 'utils/numberFormat';
import styled from '@emotion/styled';
import posthog from '@leyan/lytics';
import OperationLogs from 'components/Refund/OperationLogs';
import { getApiErrorCodeMsg } from 'services/api/utils/errorCode';

const TipRow = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  margin-bottom: 10px;
`;
const TipRowMarkLine = styled.div`
  width: 3px;
  height: 14px;
  margin-right: 10px;
  background: #3370ff;
`;
const TipRowTitle = styled.span`
  color: var(--fett-text-primary);
  font-size: 16px;
`;
const TipRowTitleTip = styled.span`
  color: var(--fett-text-tertiary);
  font-size: 12px;
  margin-left: 6px;
`;

const Row = styled.div`
  margin-left: 13px;
  span {
    margin-right: 8px;
  }
`;
const AlertChat = styled(Alert)`
  background: var(--fett-surface-warning-bg) !important;
  align-items: start;
  .ant-alert-icon {
    margin-top: 4px;
  }
  .ant-alert-message {
    > span {
      font-size: 15px;
      margin-bottom: 8px;
      display: inline-block;
    }
    .ant-badge {
      margin-bottom: 0;
    }
  }
  margin-bottom: 16px;
`;
const FormRefund = styled(Form)`
  .ant-form-item-extra {
    color: var(--fett-surface-warning-text);
  }
`;

interface IProposeDlg {
  visible: boolean;
  setVisible: React.Dispatch<React.SetStateAction<boolean>>;
  returnSn: string;
  onSuccess?: () => void;
  unit?: string;
  allowOffer: Conversation.Trade.IAllowOffer[];
  children?: React.ReactNode;
  operationLog: Conversation.Trade.IReturnOperationLog[];
  offerCounterLimit: number;
  title: string;
}

/** 提出部分退款; 再次提案; */
const ProposeDlg = (props: IProposeDlg) => {
  const { visible, setVisible } = props;
  const { t } = useTranslate();
  const [form] = Form.useForm();
  const {
    onSuccess,
    returnSn,
    unit,
    children,
    operationLog,
    offerCounterLimit,
    allowOffer,
    title,
  } = props;

  const [popVis, setPopVis] = useState(false);

  const [reqData, setReqData] = useState<ITradeOfferV1Req>();
  if (!allowOffer) {
    return null;
  }

  const offerObj = allowOffer.filter((v) => v.returnType === 'ONLY_REFUND')[0];
  if (!offerObj) {
    return null;
  }
  const { maxRefundAmount, returnType, refundAmountAdjustable } = offerObj;
  if (!refundAmountAdjustable) {
    form.setFieldsValue({ maxRefundAmount });
  }
  const offerLog = operationLog?.filter((v) => v.operate === 'OFFER');

  return (
    <Modal
      destroyOnClose
      title={title}
      centered
      visible={visible}
      onCancel={() => setVisible(false)}
      width={640}
      footer={[
        <Popconfirm
          visible={popVis}
          title={t('trade.发起提案提示', { limit: offerCounterLimit })}
          onCancel={() => setPopVis(false)}
          onConfirm={() => {
            tradeOfferV1(reqData!)
              .then(() => {
                setPopVis(false);
                setVisible(false);
                posthog.capture('$returnRst', {
                  custRreport: {
                    opType: 'OFFER',
                    status: 'ok',
                    reqData,
                    offerCounterLimit,
                  },
                });
                onSuccess?.();
              })
              .catch((e) => {
                setPopVis(false);
                posthog.capture('$returnRst', {
                  custRreport: {
                    opType: 'OFFER',
                    status: 'err',
                    reqData,
                    offerCounterLimit,
                    errTip: e.message,
                  },
                });
                message.error(getApiErrorCodeMsg(e.response));
              });
          }}
        >
          <Button
            key="submit"
            type="primary"
            onClick={() => {
              form
                .validateFields()
                .then((values) => {
                  setPopVis(true);
                  setReqData({
                    returnExternalId: returnSn,
                    returnType,
                    refundAmount: parseFloat(values.maxRefundAmount, 10),
                  });
                })
                .catch(() => {});
            }}
          >
            {t('trade.发起提案')}
          </Button>
        </Popconfirm>,
      ]}
    >
      {children ? (
        <>
          <AlertChat
            message={
              <>
                <span>{t('trade.shopeeOfficialRefundWarning')}</span>
                {children}
              </>
            }
            type="warning"
            showIcon
            banner
          />
        </>
      ) : null}

      <TipRow>
        <TipRowMarkLine />
        <TipRowTitle>{t('trade.买家提出')}</TipRowTitle>
      </TipRow>
      <Row>
        <span>{t('trade.提案')}: </span> {t('trade.退款')}
      </Row>
      <Row>
        <span>{t('trade.退款金额')}: </span> {`${unit}${formatNumber(maxRefundAmount, '0,0.00')}`}
      </Row>
      <TipRow style={{ marginTop: '16px' }}>
        <TipRowMarkLine />
        <TipRowTitle>{t('trade.更改提案')}</TipRowTitle>
      </TipRow>
      <FormRefund form={form} layout="vertical" autoComplete="off" preserve={false}>
        <Form.Item
          name="maxRefundAmount"
          label={t('trade.退款金额')}
          rules={[
            {
              type: 'number',
              transform: (val) => parseFloat(val),
              required: true,
              message: t('trade.可退金额必填'),
            },
            {
              type: 'number',
              max: maxRefundAmount,
              transform: (val) => (val ? parseFloat(val) : 0),
              message: t('trade.超出最大可退金额'),
            },
            {
              type: 'number',
              min: 0.01,
              transform: (val) => (val ? parseFloat(val) : 0),
              message: t('trade.超出最小可退金额'),
            },
          ]}
          extra={t('trade.最大可退金额', {
            money: `${unit}${formatNumber(maxRefundAmount, '0,0.00')}`,
          })}
        >
          <Input
            disabled={!refundAmountAdjustable}
            placeholder={t('trade.请输入')}
            style={{ width: '200px' }}
            type="number"
            min={0.01}
            suffix={unit}
          />
        </Form.Item>
      </FormRefund>
      {offerLog?.length > 0 ? (
        <>
          <TipRow>
            <TipRowMarkLine />
            <TipRowTitle>{t('trade.操作记录')}</TipRowTitle>
            <TipRowTitleTip>{t('trade.操作记录提示_copy')}</TipRowTitleTip>
          </TipRow>
          <OperationLogs unit={unit!} operationLog={offerLog} />
        </>
      ) : null}
    </Modal>
  );
};

export default ProposeDlg;
